import { collection, addDoc, getDocs, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';

export interface NewsletterSubscription {
  email: string;
  subscribedAt: Timestamp;
  source?: string; // Where the subscription came from (e.g., 'footer', 'popup')
}

/**
 * Subscribe an email to the newsletter
 * @param email Email address to subscribe
 * @param source Source of the subscription (optional)
 * @returns Promise resolving to true if subscription was successful
 */
export const subscribeToNewsletter = async (email: string, source?: string): Promise<boolean> => {
  try {
    // For public users, we can't check if the email already exists due to Firestore rules
    // Instead, we'll try to add the subscription directly and handle any errors
    
    // Add new subscription
    const subscription: NewsletterSubscription = {
      email,
      subscribedAt: Timestamp.now(),
      source
    };
    
    await addDoc(collection(db, 'newsletter-subscribers'), subscription);
    return true;
  } catch (error) {
    console.error('Error subscribing to newsletter:', error);
    return false;
  }
};

/**
 * Get all newsletter subscribers (admin only)
 * @returns Promise resolving to array of newsletter subscriptions
 */
export const getNewsletterSubscribers = async (): Promise<NewsletterSubscription[]> => {
  try {
    const subscribersSnapshot = await getDocs(collection(db, 'newsletter-subscribers'));
    
    return subscribersSnapshot.docs.map(doc => ({
      ...doc.data() as NewsletterSubscription,
      id: doc.id
    }));
  } catch (error) {
    console.error('Error getting newsletter subscribers:', error);
    return [];
  }
};

/**
 * Get count of newsletter subscribers
 * @returns Promise resolving to count of subscribers
 */
export const getNewsletterSubscriberCount = async (): Promise<number> => {
  try {
    const subscribersSnapshot = await getDocs(collection(db, 'newsletter-subscribers'));
    return subscribersSnapshot.size;
  } catch (error) {
    console.error('Error getting newsletter subscriber count:', error);
    return 0;
  }
};
